// apps/roleforge-app/src/lib/bundle-download.ts
//
// Shared Blueprint Bundle download behavior — one place for the blob-save
// sequence, the buyer-readable failure copy, and the busy/error state
// machine. Consumed by both ExportAssetsLink (Activation Moment) and the
// delivery page (AuditDashboardPage). Deliberately minimal: this is the
// bundle download, not a generic file-download framework.

import { useState } from 'react';
import { useAuth } from './auth';
import { downloadExportBundle } from './api-client';

export type ExportError = { message: string; retryable: boolean };

// Issue #1326 (4) — `downloadExportBundle` throws `ApiError` on any non-2xx
// and the app has no ErrorBoundary / `unhandledrejection` handler, so a
// swallowed rejection reads as the product not delivering. Every consumer
// must surface this copy.
export function describeExportFailure(err: unknown): ExportError {
  const status = (err as { status?: unknown })?.status;
  if (status === 503 || status === 429) {
    return {
      message:
        'The download service is busy right now. Nothing is lost — wait a moment and try again.',
      retryable: true,
    };
  }
  if (typeof status === 'number' && status >= 500) {
    return {
      message:
        'We could not build your download just now. This has been reported — please try again.',
      retryable: true,
    };
  }
  if (status === 404) {
    return {
      message: 'There is nothing to export for this audit yet.',
      retryable: false,
    };
  }
  if (status === 401 || status === 403) {
    return {
      message: 'Your session expired. Sign in again, then export your assets.',
      retryable: false,
    };
  }
  return {
    message: 'The download did not start. Check your connection and try again.',
    retryable: true,
  };
}

/** Streams a blob to the user's disk via a synthetic anchor click. */
export function saveBlobAsFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Busy/error/download state for one audit's bundle. `download()` is a no-op
 * while a download is in flight or when there is no session yet.
 */
export function useBundleDownload(auditId: string): {
  busy: boolean;
  error: ExportError | null;
  download: () => Promise<void>;
} {
  const { jwt } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ExportError | null>(null);

  const download = async () => {
    if (!jwt || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { blob, filename } = await downloadExportBundle(jwt, auditId);
      saveBlobAsFile(blob, filename);
    } catch (err) {
      setError(describeExportFailure(err));
    } finally {
      setBusy(false);
    }
  };

  return { busy, error, download };
}
